import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Clock, Star, Loader2, Shield, LogOut, 
  Users, MessageSquare, ChefHat, UtensilsCrossed, BarChart3,
  DollarSign, Eye, Calendar, UserCog, Search, KeyRound, Mail, FileText,
  ImagePlus, Trash2, Plus, Video, Upload
} from 'lucide-react';
import ContentManagement from '../components/admin/ContentManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { token, user, logout } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [reviews, setReviews] = useState([]);
  const [applications, setApplications] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [users, setUsers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectType, setRejectType] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [mediaModal, setMediaModal] = useState(null);
  const [mediaImages, setMediaImages] = useState([]);
  const [mediaVideo, setMediaVideo] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaSaving, setMediaSaving] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    } else if (activeTab === 'reviews') {
      fetchReviews();
    } else if (activeTab === 'experiences') {
      fetchExperiences();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchApplications();
    }
  }, [token, filter, activeTab, userFilter, navigate]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load dashboard stats');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userFilter !== 'all') params.append('role', userFilter);
      if (userSearch) params.append('search', userSearch);
      
      const res = await axios.get(`${API_URL}/api/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/reviews?status_filter=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const res = await axios.get(`${API_URL}/api/admin/host-applications${statusParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load applications');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const res = await axios.get(`${API_URL}/api/admin/experiences${statusParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExperiences(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load experiences');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (reviewId, action) => {
    setActionLoading(prev => ({ ...prev, [reviewId]: action }));
    try {
      await axios.put(`${API_URL}/api/admin/reviews/${reviewId}?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Review ${action}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [reviewId]: null }));
    }
  };

  const handleApplicationAction = async (applicationId, status, notes = null) => {
    setActionLoading(prev => ({ ...prev, [applicationId]: status }));
    try {
      await axios.put(`${API_URL}/api/admin/host-applications/${applicationId}`, {
        status,
        admin_notes: notes,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Application ${status}`);
      setApplications(prev => prev.filter(a => a.id !== applicationId));
      setRejectModal(null);
      setRejectNotes('');
      setRejectType(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [applicationId]: null }));
    }
  };

  const handleExperienceAction = async (experienceId, status, notes = null) => {
    setActionLoading(prev => ({ ...prev, [experienceId]: status }));
    try {
      await axios.put(`${API_URL}/api/admin/experiences/${experienceId}`, {
        status,
        admin_notes: notes,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Experience ${status}`);
      setExperiences(prev => prev.filter(e => e.id !== experienceId));
      setRejectModal(null);
      setRejectNotes('');
      setRejectType(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [experienceId]: null }));
    }
  };

  const handleResetCredentials = async () => {
    if (!resetEmail && !resetPassword) {
      toast.error('Please provide email or password to reset');
      return;
    }

    setActionLoading(prev => ({ ...prev, [resetModal.id]: 'reset' }));
    try {
      const data = {};
      if (resetEmail) data.new_email = resetEmail;
      if (resetPassword) data.new_password = resetPassword;

      await axios.put(`${API_URL}/api/admin/users/${resetModal.id}/reset-credentials`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User credentials updated successfully');
      setResetModal(null);
      setResetEmail('');
      setResetPassword('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset credentials');
    } finally {
      setActionLoading(prev => ({ ...prev, [resetModal?.id]: null }));
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(prev => ({ ...prev, [`role-${userId}`]: true }));
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setActionLoading(prev => ({ ...prev, [`role-${userId}`]: false }));
    }
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  // ── Media Editor Functions ──
  const openMediaEditor = (exp) => {
    setMediaModal(exp);
    setMediaImages(exp.images || []);
    setMediaVideo(exp.video || '');
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setMediaUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}`;
        if (res.data.type === 'video') {
          setMediaVideo(url);
        } else {
          setMediaImages(prev => [...prev, url]);
        }
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setMediaUploading(false);
      e.target.value = '';
    }
  };

  const removeMediaImage = (index) => {
    setMediaImages(prev => prev.filter((_, i) => i !== index));
  };

  const saveMediaChanges = async () => {
    if (!mediaModal) return;
    setMediaSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/experiences/${mediaModal.id}/media`, {
        images: mediaImages,
        video: mediaVideo || null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Media updated successfully');
      setMediaModal(null);
      fetchExperiences();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update media');
    } finally {
      setMediaSaving(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-50 text-yellow-700',
    approved: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-700',
  };

  const roleColors = {
    user: 'bg-blue-50 text-blue-700',
    host: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className="min-h-screen bg-[#F4EDE4]" data-testid="admin-page">
      <Toaster />
      <header className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#D65A31]" />
            <div>
              <h1 className="font-semibold text-lg">MealHoster Admin</h1>
              <p className="text-gray-400 text-xs">Administrator Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Tab Navigation - Scrollable on mobile */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 border-t border-gray-700 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <button
              onClick={() => { setActiveTab('dashboard'); }}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'dashboard' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Stats</span>
            </button>
            <button
              onClick={() => { setActiveTab('applications'); setFilter('pending'); }}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'applications' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <ChefHat className="w-4 h-4" />
              <span className="hidden sm:inline">Host Applications</span>
              <span className="sm:hidden">Hosts</span>
            </button>
            <button
              onClick={() => { setActiveTab('experiences'); setFilter('pending'); }}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'experiences' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <UtensilsCrossed className="w-4 h-4" />
              <span className="hidden sm:inline">Experience Approvals</span>
              <span className="sm:hidden">Exp.</span>
            </button>
            <button
              onClick={() => { setActiveTab('reviews'); setFilter('pending'); }}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'reviews' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Review Moderation</span>
              <span className="sm:hidden">Reviews</span>
            </button>
            <button
              onClick={() => { setActiveTab('users'); setUserFilter('all'); }}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'users' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <UserCog className="w-4 h-4" />
              <span className="hidden sm:inline">User Management</span>
              <span className="sm:hidden">Users</span>
            </button>
            <button
              onClick={() => { setActiveTab('content'); }}
              className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'content' 
                  ? 'border-[#D65A31] text-white' 
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <FileText className="w-4 h-4" />
              Content Management
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : dashboardStats && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Platform Overview</h2>
              
              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('experiences'); setFilter('all'); }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UtensilsCrossed className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-500">Total Experiences</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{dashboardStats.total_experiences}</p>
                  <p className="text-xs text-gray-500 mt-1">{dashboardStats.active_experiences} active</p>
                </div>

                <div className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('experiences'); setFilter('all'); }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-500">Total Bookings</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{dashboardStats.total_bookings}</p>
                  <p className="text-xs text-gray-500 mt-1">{dashboardStats.confirmed_bookings} confirmed</p>
                </div>

                <div className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('experiences'); setFilter('all'); }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-yellow-600" />
                    </div>
                    <span className="text-sm text-gray-500">Total Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatPrice(dashboardStats.total_revenue)}</p>
                  <p className="text-xs text-gray-500 mt-1">Platform earnings</p>
                </div>

                <div className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('experiences'); setFilter('all'); }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm text-gray-500">Total Views</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{dashboardStats.total_views.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Experience views</p>
                </div>
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('users'); setUserFilter('all'); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Total Users</span>
                    <Users className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{dashboardStats.total_users}</p>
                </div>

                <div className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('users'); setUserFilter('host'); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Total Hosts</span>
                    <ChefHat className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{dashboardStats.total_hosts}</p>
                </div>

                <div className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('experiences'); setFilter('pending'); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Pending Experiences</span>
                    <Clock className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-2xl font-bold text-yellow-600 mt-2">{dashboardStats.pending_experiences}</p>
                </div>

                <div className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('reviews'); setFilter('pending'); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Pending Reviews</span>
                    <MessageSquare className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-2xl font-bold text-yellow-600 mt-2">{dashboardStats.pending_reviews}</p>
                </div>

                <div className="bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('applications'); setFilter('pending'); }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Pending Host Apps</span>
                    <ChefHat className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-2xl font-bold text-yellow-600 mt-2">{dashboardStats.pending_host_applications || 0}</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => { setActiveTab('applications'); setFilter('pending'); }}
                    className="bg-[#D65A31] hover:bg-[#c04d28]"
                  >
                    <ChefHat className="w-4 h-4 mr-2" />
                    Review Host Applications ({dashboardStats.pending_host_applications || 0})
                  </Button>
                  <Button 
                    onClick={() => { setActiveTab('experiences'); setFilter('pending'); }}
                    variant="outline"
                  >
                    <UtensilsCrossed className="w-4 h-4 mr-2" />
                    Review Pending Experiences ({dashboardStats.pending_experiences})
                  </Button>
                  <Button 
                    onClick={() => { setActiveTab('reviews'); setFilter('pending'); }}
                    variant="outline"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Moderate Reviews ({dashboardStats.pending_reviews})
                  </Button>
                  <Button 
                    onClick={() => { setActiveTab('users'); }}
                    variant="outline"
                  >
                    <UserCog className="w-4 h-4 mr-2" />
                    Manage Users
                  </Button>
                </div>
              </div>
            </div>
          )
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <>
            {/* User Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex gap-2">
                {['all', 'user', 'host'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setUserFilter(f)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      userFilter === f ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border'
                    }`}
                  >
                    {f === 'all' ? 'All Users' : f === 'user' ? 'Guests' : 'Hosts'}
                  </button>
                ))}
              </div>
              <form onSubmit={handleUserSearch} className="flex gap-2 flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : users.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No users found</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-5 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{u.name}</p>
                              <p className="text-sm text-gray-500">{u.email}</p>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <select
                              value={u.role}
                              onChange={(e) => handleRoleChange(u.id, e.target.value)}
                              disabled={actionLoading[`role-${u.id}`]}
                              className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${roleColors[u.role]}`}
                            >
                              <option value="user">Guest</option>
                              <option value="host">Host</option>
                            </select>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${u.email_verified ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                              {u.email_verified ? 'Verified' : 'Unverified'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-500">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setResetModal(u)}
                              className="text-xs"
                            >
                              <KeyRound className="w-3 h-3 mr-1" />
                              Reset Credentials
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Content Management Tab */}
        {activeTab === 'content' && (
          <ContentManagement token={token} />
        )}

        {/* Other Tabs (Applications, Experiences, Reviews) */}
        {(activeTab === 'applications' || activeTab === 'experiences' || activeTab === 'reviews') && (
          <>
            {/* Filter tabs */}
            <div className="flex gap-2 mb-6" data-testid="filter-tabs">
              {['pending', 'approved', 'rejected', 'all'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                    filter === f ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border'
                  }`}
                  data-testid={`filter-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : activeTab === 'applications' ? (
              /* Host Applications List */
              applications.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <ChefHat className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No {filter === 'all' ? '' : filter} host applications</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="applications-list">
                  {applications.map((app) => (
                    <div key={app.id} className="bg-white rounded-xl border p-5" data-testid={`application-${app.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold">{app.user_name}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusColors[app.status]}`}>
                              {app.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{app.user_email}</p>
                          
                          <div className="bg-gray-50 rounded-lg p-4 mb-3">
                            <h4 className="font-medium mb-2">{app.title}</h4>
                            <p className="text-sm text-gray-600 mb-3">{app.description}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
                              <div><strong>Type:</strong> {app.experience_type}</div>
                              <div><strong>Location:</strong> {app.location}</div>
                              <div><strong>Cuisine:</strong> {app.cuisine}</div>
                              <div><strong>Price:</strong> {formatPrice(app.price)}/person</div>
                              <div><strong>Max Guests:</strong> {app.max_guests}</div>
                              <div><strong>Duration:</strong> {app.duration}</div>
                              <div><strong>Category:</strong> {app.category}</div>
                              <div><strong>Meal Type:</strong> {app.meal_type}</div>
                            </div>
                            {(app.proof_of_address || app.id_document) && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs font-medium text-gray-500 mb-2">Verification Documents</p>
                                <div className="flex gap-3">
                                  {app.proof_of_address && (
                                    <a href={app.proof_of_address.startsWith('http') ? app.proof_of_address : `${API_URL}${app.proof_of_address}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D65A31] underline">
                                      View Proof of Address
                                    </a>
                                  )}
                                  {app.id_document && (
                                    <a href={app.id_document.startsWith('http') ? app.id_document : `${API_URL}${app.id_document}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D65A31] underline">
                                      View ID Document
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}
                            {app.images?.length > 0 && (
                              <div className="flex gap-2 mt-3">
                                {app.images.slice(0, 4).map((img) => (
                                  <img key={img} src={img} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                ))}
                                {app.images.length > 4 && (
                                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                                    +{app.images.length - 4}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-400">
                            Submitted: {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        
                        {app.status === 'pending' && (
                          <div className="flex flex-col gap-2 shrink-0">
                            <Button
                              size="sm"
                              onClick={() => handleApplicationAction(app.id, 'approved')}
                              disabled={!!actionLoading[app.id]}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-app-${app.id}`}
                            >
                              {actionLoading[app.id] === 'approved' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><CheckCircle className="w-4 h-4 mr-1" />Approve</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setRejectModal(app); setRejectType('application'); }}
                              disabled={!!actionLoading[app.id]}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`reject-app-${app.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : activeTab === 'experiences' ? (
              /* Experience Approvals List */
              experiences.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <UtensilsCrossed className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No {filter === 'all' ? '' : filter} experiences</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="experiences-list">
                  {experiences.map((exp) => (
                    <div key={exp.id} className="bg-white rounded-xl border p-5" data-testid={`experience-${exp.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold">{exp.title}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusColors[exp.approval_status]}`}>
                              {exp.approval_status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">
                            Host: <span className="font-medium">{exp.host_name}</span> ({exp.host_email})
                          </p>
                          
                          <div className="bg-gray-50 rounded-lg p-4 mb-3">
                            <p className="text-sm text-gray-600 mb-3">{exp.description}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
                              <div><strong>Category:</strong> {exp.category}</div>
                              <div><strong>Location:</strong> {exp.location}</div>
                              <div><strong>Cuisine:</strong> {exp.cuisine}</div>
                              <div><strong>Price:</strong> {formatPrice(exp.price)}/person</div>
                              <div><strong>Max Guests:</strong> {exp.max_guests}</div>
                              <div><strong>Duration:</strong> {exp.duration}</div>
                              <div><strong>Meal Type:</strong> {exp.meal_type}</div>
                            </div>
                            {exp.images?.length > 0 && (
                              <div className="flex gap-2 mt-3">
                                {exp.images.slice(0, 5).map((img) => (
                                  <img key={img} src={img} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-400">
                            Created: {new Date(exp.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMediaEditor(exp)}
                            className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-media-${exp.id}`}
                          >
                            <ImagePlus className="w-4 h-4 mr-1" />Edit Media
                          </Button>
                          {exp.approval_status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleExperienceAction(exp.id, 'approved')}
                                disabled={!!actionLoading[exp.id]}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-exp-${exp.id}`}
                              >
                                {actionLoading[exp.id] === 'approved' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <><CheckCircle className="w-4 h-4 mr-1" />Approve</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setRejectModal(exp); setRejectType('experience'); }}
                                disabled={!!actionLoading[exp.id]}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`reject-exp-${exp.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              /* Reviews List */
              reviews.length === 0 ? (
                <div className="bg-white rounded-xl border p-12 text-center">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No {filter === 'all' ? '' : filter} reviews</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-white rounded-xl border p-5" data-testid={`review-${review.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-sm">{review.user_name}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusColors[review.status]}`}>
                              {review.status}
                            </span>
                            <div className="flex items-center gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={`star-${i}`} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-200'}`} />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{review.comment}</p>
                          <p className="text-xs text-gray-400">
                            Experience: <span className="font-medium text-gray-600">{review.experience_title}</span>
                            {' · '}
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {review.status === 'pending' && (
                          <div className="flex gap-2 shrink-0">
                            <Button
                              size="sm"
                              onClick={() => handleReviewAction(review.id, 'approved')}
                              disabled={!!actionLoading[review.id]}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${review.id}`}
                            >
                              {actionLoading[review.id] === 'approved' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" />Approve</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReviewAction(review.id, 'rejected')}
                              disabled={!!actionLoading[review.id]}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`reject-${review.id}`}
                            >
                              {actionLoading[review.id] === 'rejected' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" />Reject</>}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </main>

      {/* Reject Modal (for both Applications and Experiences) */}
      <Dialog open={!!rejectModal && rejectType !== null} onOpenChange={() => { setRejectModal(null); setRejectNotes(''); setRejectType(null); }}>
        <DialogContent className="max-w-md">
          <DialogTitle>
            {rejectType === 'experience' ? 'Reject Experience' : 'Reject Host Application'}
          </DialogTitle>
          <DialogDescription>
            {rejectType === 'experience' 
              ? `Provide a reason for rejecting "${rejectModal?.title}". This will be shared with the host.`
              : `Provide a reason for rejecting ${rejectModal?.user_name}'s application. This will be shared with the applicant.`
            }
          </DialogDescription>
          <Textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="Reason for rejection (optional)..."
            rows={4}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setRejectModal(null); setRejectNotes(''); setRejectType(null); }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (rejectType === 'experience') {
                  handleExperienceAction(rejectModal.id, 'rejected', rejectNotes);
                } else {
                  handleApplicationAction(rejectModal.id, 'rejected', rejectNotes);
                }
              }}
              disabled={actionLoading[rejectModal?.id]}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {actionLoading[rejectModal?.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {rejectType === 'experience' ? 'Reject Experience' : 'Reject Application'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Credentials Modal */}
      <Dialog open={!!resetModal} onOpenChange={() => { setResetModal(null); setResetEmail(''); setResetPassword(''); }}>
        <DialogContent className="max-w-md">
          <DialogTitle>Reset User Credentials</DialogTitle>
          <DialogDescription>
            Reset email (username) and/or password for <strong>{resetModal?.name}</strong> ({resetModal?.email})
          </DialogDescription>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                <Mail className="w-4 h-4 inline mr-1" />
                New Email (Username)
              </label>
              <Input
                type="email"
                placeholder="Leave empty to keep current email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                <KeyRound className="w-4 h-4 inline mr-1" />
                New Password
              </label>
              <Input
                type="password"
                placeholder="Leave empty to keep current password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => { setResetModal(null); setResetEmail(''); setResetPassword(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleResetCredentials}
              disabled={actionLoading[resetModal?.id] === 'reset' || (!resetEmail && !resetPassword)}
              className="bg-[#D65A31] hover:bg-[#c04d28]"
            >
              {actionLoading[resetModal?.id] === 'reset' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Reset Credentials
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Editor Dialog */}
      <Dialog open={!!mediaModal} onOpenChange={(open) => { if (!open) setMediaModal(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-lg font-semibold">
            Edit Media — {mediaModal?.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mb-4">
            Add, remove, or reorder images and video for this experience.
          </DialogDescription>

          {/* Current Images */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Images ({mediaImages.length})</label>
            {mediaImages.length > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                {mediaImages.map((img, idx) => (
                  <div key={`${img}-${idx}`} className="relative group rounded-lg overflow-hidden border">
                    <img src={img} alt={`Image ${idx + 1}`} className="w-full h-28 object-cover" />
                    <button
                      onClick={() => removeMediaImage(idx)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${idx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                      {idx + 1}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No images</p>
            )}

            {/* Upload Button */}
            <div className="flex gap-2">
              <label
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium transition-colors"
                data-testid="upload-media-btn"
              >
                <Upload className="w-4 h-4" />
                {mediaUploading ? 'Uploading...' : 'Upload Images/Video'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                  multiple
                  onChange={handleMediaUpload}
                  disabled={mediaUploading}
                  className="hidden"
                />
              </label>
              {mediaUploading && <Loader2 className="w-5 h-5 animate-spin text-gray-400 self-center" />}
            </div>

            {/* Add image by URL */}
            <div className="flex gap-2">
              <Input
                placeholder="Or paste an image URL..."
                id="media-url-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setMediaImages(prev => [...prev, e.target.value.trim()]);
                    e.target.value = '';
                  }
                }}
                className="flex-1"
                data-testid="media-url-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.getElementById('media-url-input');
                  if (input?.value.trim()) {
                    setMediaImages(prev => [...prev, input.value.trim()]);
                    input.value = '';
                  }
                }}
                data-testid="add-url-btn"
              >
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </div>

            {/* Video */}
            <div className="pt-3 border-t">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Video className="w-4 h-4" /> Video URL
              </label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={mediaVideo}
                  onChange={(e) => setMediaVideo(e.target.value)}
                  placeholder="Video URL (optional)"
                  className="flex-1"
                  data-testid="media-video-input"
                />
                {mediaVideo && (
                  <Button variant="outline" size="sm" onClick={() => setMediaVideo('')}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setMediaModal(null)} data-testid="cancel-media-btn">
              Cancel
            </Button>
            <Button
              onClick={saveMediaChanges}
              disabled={mediaSaving}
              className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
              data-testid="save-media-btn"
            >
              {mediaSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
